/**
 * Storefront Identity Section
 *
 * iter-3b — Collects the v3 identity fields the storefront-v3 schema layer
 * reads (Tour, Organization, TouristInformationCenter JSON-LD).
 *
 * All fields are Free-tier. Anything left blank is omitted from JSON-LD
 * rather than fabricated (strict no-invented-data rule).
 */

import { useEffect, useState } from 'react'
import api from '@/services/api'
import BokunPrefillBanner, { RequiredForStorefrontBadge } from './BokunPrefillBanner'

const SOCIAL_PLATFORMS: Array<{ key: SocialKey; label: string; placeholder: string }> = [
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://www.linkedin.com/company/your-company' },
  { key: 'instagram', label: 'Instagram', placeholder: 'https://www.instagram.com/your-handle' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://www.facebook.com/your-page' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://www.youtube.com/@your-channel' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://www.tiktok.com/@your-handle' },
  { key: 'x', label: 'X (Twitter)', placeholder: 'https://x.com/your-handle' },
  { key: 'threads', label: 'Threads', placeholder: 'https://www.threads.net/@your-handle' },
]

type SocialKey = 'linkedin' | 'instagram' | 'facebook' | 'youtube' | 'tiktok' | 'x' | 'threads'

interface PressArticle {
  title: string
  publisher?: string
  url: string
  publishedAt?: string
}

interface IdentityForm {
  tradingName: string
  founderName: string
  foundingYear: string
  destinationsRaw: string
  operatingLanguagesRaw: string
  press: PressArticle[]
  socialLinks: Record<SocialKey, string>
}

const EMPTY_FORM: IdentityForm = {
  tradingName: '',
  founderName: '',
  foundingYear: '',
  destinationsRaw: '',
  operatingLanguagesRaw: '',
  press: [],
  socialLinks: {
    linkedin: '',
    instagram: '',
    facebook: '',
    youtube: '',
    tiktok: '',
    x: '',
    threads: '',
  },
}

interface Props {
  vendorId: string
}

export default function StorefrontIdentitySection({ vendorId }: Props) {
  const [form, setForm] = useState<IdentityForm>(EMPTY_FORM)
  const [legalName, setLegalName] = useState<string | undefined>(undefined)
  const [isBokunVendor, setIsBokunVendor] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!vendorId) return

    api.vendors.getCheckoutConfig(vendorId)
      .then((res) => {
        const payload = res.data?.data || res.data || {}
        const identity = payload.identity
        const bokunConnected = !!payload.integrations?.bokun?.connected
        setIsBokunVendor(bokunConnected)
        if (!identity) return
        setLegalName(identity.legal_name || undefined)
        setForm({
          tradingName: identity.trading_name || '',
          founderName: identity.founder_name || '',
          foundingYear: identity.founding_year ? String(identity.founding_year) : '',
          destinationsRaw: (identity.destinations || []).join(', '),
          operatingLanguagesRaw: (identity.operating_languages || []).join(', '),
          press: (identity.press || []).map((p: any) => ({
            title: p.title || '',
            publisher: p.publisher || '',
            url: p.url || '',
            publishedAt: p.published_at || '',
          })),
          socialLinks: {
            linkedin: identity.social_links?.linkedin || '',
            instagram: identity.social_links?.instagram || '',
            facebook: identity.social_links?.facebook || '',
            youtube: identity.social_links?.youtube || '',
            tiktok: identity.social_links?.tiktok || '',
            x: identity.social_links?.x || '',
            threads: identity.social_links?.threads || '',
          },
        })
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [vendorId])

  const update = <K extends keyof IdentityForm>(field: K, value: IdentityForm[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    setError(null)
    setSuccess(null)
  }

  const updateSocial = (key: SocialKey, value: string) => {
    setForm((prev) => ({ ...prev, socialLinks: { ...prev.socialLinks, [key]: value } }))
    setError(null)
    setSuccess(null)
  }

  const updatePress = (index: number, patch: Partial<PressArticle>) => {
    setForm((prev) => {
      const next = [...prev.press]
      next[index] = { ...next[index], ...patch }
      return { ...prev, press: next }
    })
    setError(null)
    setSuccess(null)
  }

  const addPress = () => {
    if (form.press.length >= 20) return
    setForm((prev) => ({
      ...prev,
      press: [...prev.press, { title: '', publisher: '', url: '', publishedAt: '' }],
    }))
  }

  const removePress = (index: number) => {
    setForm((prev) => ({ ...prev, press: prev.press.filter((_, i) => i !== index) }))
  }

  const handleSave = async () => {
    if (!vendorId) return

    setError(null)
    setSuccess(null)
    setSaving(true)

    try {
      // Convert raw comma-separated lists to arrays. Blank entries drop.
      const destinations = form.destinationsRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
      const operatingLanguages = form.operatingLanguagesRaw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)

      // Drop press entries with no title or url (incomplete) before sending.
      const press = form.press
        .filter((p) => p.title.trim() && p.url.trim())
        .map((p) => ({
          title: p.title.trim(),
          publisher: p.publisher?.trim() || undefined,
          url: p.url.trim(),
          publishedAt: p.publishedAt || undefined,
        }))

      // Send all social platforms even when blank so the API can clear
      // a platform the vendor has removed.
      const socialLinks: Record<string, string> = {}
      for (const [k, v] of Object.entries(form.socialLinks)) {
        socialLinks[k] = (v || '').trim()
      }

      const payload: Record<string, unknown> = {
        tradingName: form.tradingName.trim(),
        founderName: form.founderName.trim(),
        foundingYear: form.foundingYear.trim() ? parseInt(form.foundingYear, 10) : null,
        destinations,
        operatingLanguages,
        press,
        socialLinks,
      }

      await api.vendors.updateIdentity(vendorId, payload)
      setSuccess('Storefront identity saved successfully')
    } catch (err: any) {
      const message =
        err.response?.data?.error?.message ||
        err.response?.data?.message ||
        'Failed to save storefront identity'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="card">
        <h3 style={{ marginBottom: '0.5rem' }}>Storefront Identity</h3>
        <p>Loading...</p>
      </div>
    )
  }

  return (
    <div className="card">
      <h3 style={{ marginBottom: '0.5rem' }}>Storefront Identity</h3>
      <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Identity fields shown to AI assistants and search engines via structured data.
        Anything left blank is omitted rather than guessed.
      </p>

      <BokunPrefillBanner show={isBokunVendor} />

      {isBokunVendor && !legalName && (
        <div
          role="note"
          style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            background: 'var(--color-warning-bg, #fff7e6)',
            border: '1px solid var(--color-warning-border, #f5d99a)',
            borderRadius: '6px',
            color: 'var(--color-warning-text, #8a5a00)',
            fontSize: '0.85rem',
            lineHeight: 1.45,
          }}
        >
          <strong style={{ fontWeight: 600 }}>Complete your registered legal name.</strong>{' '}
          Bokun supplies your trading name, but not your registered legal name. Add it so
          your storefront’s structured data, invoices, and checkout terms reference the
          correct entity. Contact support to set this field.
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem' }}>
        {legalName && (
          <div>
            <label style={labelStyle}>Registered Legal Name</label>
            <input type="text" value={legalName} readOnly style={{ ...inputStyle, background: 'var(--color-gray-50)' }} />
            <p style={hintStyle}>Set during signup. Contact support to change.</p>
          </div>
        )}

        <div>
          <label style={labelStyle}>Trading Name</label>
          <input
            type="text"
            value={form.tradingName}
            onChange={(e) => update('tradingName', e.target.value)}
            placeholder="The public name customers know you by"
            style={inputStyle}
            maxLength={255}
          />
          <p style={hintStyle}>Used as the primary name in storefront titles when set.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '1rem' }}>
          <div>
            <label style={labelStyle}>
              Founder Name
              <RequiredForStorefrontBadge show={isBokunVendor} />
            </label>
            <input
              type="text"
              value={form.founderName}
              onChange={(e) => update('founderName', e.target.value)}
              placeholder="Full name"
              style={inputStyle}
              maxLength={255}
            />
          </div>

          <div>
            <label style={labelStyle}>
              Founding Year
              <RequiredForStorefrontBadge show={isBokunVendor} />
            </label>
            <input
              type="number"
              value={form.foundingYear}
              onChange={(e) => update('foundingYear', e.target.value)}
              placeholder="2018"
              style={{ ...inputStyle, width: '160px' }}
              min={1800}
              max={2100}
            />
          </div>
        </div>

        <div>
          <label style={labelStyle}>Destinations Served</label>
          <input
            type="text"
            value={form.destinationsRaw}
            onChange={(e) => update('destinationsRaw', e.target.value)}
            placeholder="Athens, Mykonos, Santorini"
            style={inputStyle}
          />
          <p style={hintStyle}>Comma-separated city names. Up to 50.</p>
        </div>

        <div>
          <label style={labelStyle}>Operating Languages</label>
          <input
            type="text"
            value={form.operatingLanguagesRaw}
            onChange={(e) => update('operatingLanguagesRaw', e.target.value)}
            placeholder="en, en-GB, el, fr"
            style={inputStyle}
          />
          <p style={hintStyle}>BCP-47 language tags, comma-separated. Up to 20.</p>
        </div>

        <div>
          <h4 style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.95rem' }}>Social Profiles</h4>
          <p style={hintStyle}>Public links only. Drives Organization.sameAs in structured data.</p>
          <div style={{ display: 'grid', gap: '0.75rem', marginTop: '0.75rem' }}>
            {SOCIAL_PLATFORMS.map(({ key, label, placeholder }) => (
              <div key={key}>
                <label style={labelStyle}>{label}</label>
                <input
                  type="url"
                  value={form.socialLinks[key]}
                  onChange={(e) => updateSocial(key, e.target.value)}
                  placeholder={placeholder}
                  style={inputStyle}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 style={{ marginTop: '1rem', marginBottom: '0.5rem', fontSize: '0.95rem' }}>Press Coverage</h4>
          <p style={hintStyle}>
            Articles or features about your business. Drives Organization.subjectOf in structured data.
          </p>

          {form.press.length === 0 && (
            <p style={{ ...hintStyle, fontStyle: 'italic', marginTop: '0.5rem' }}>No press articles added yet.</p>
          )}

          {form.press.map((article, idx) => (
            <div
              key={idx}
              style={{
                border: '1px solid var(--color-gray-200)',
                borderRadius: '6px',
                padding: '0.75rem',
                marginTop: '0.75rem',
                display: 'grid',
                gap: '0.5rem',
              }}
            >
              <input
                type="text"
                value={article.title}
                onChange={(e) => updatePress(idx, { title: e.target.value })}
                placeholder="Article title"
                style={inputStyle}
                maxLength={240}
              />
              <input
                type="text"
                value={article.publisher || ''}
                onChange={(e) => updatePress(idx, { publisher: e.target.value })}
                placeholder="Publisher (Bloomberg, BBC, etc.)"
                style={inputStyle}
                maxLength={120}
              />
              <input
                type="url"
                value={article.url}
                onChange={(e) => updatePress(idx, { url: e.target.value })}
                placeholder="https://..."
                style={inputStyle}
              />
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input
                  type="date"
                  value={article.publishedAt || ''}
                  onChange={(e) => updatePress(idx, { publishedAt: e.target.value })}
                  style={{ ...inputStyle, width: '180px' }}
                />
                <button
                  type="button"
                  onClick={() => removePress(idx)}
                  style={{
                    marginLeft: 'auto',
                    padding: '0.4rem 0.75rem',
                    background: 'var(--color-gray-100)',
                    color: 'var(--color-gray-700)',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                  }}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}

          {form.press.length < 20 && (
            <button
              type="button"
              onClick={addPress}
              style={{
                marginTop: '0.75rem',
                padding: '0.4rem 0.75rem',
                background: 'transparent',
                color: 'var(--color-tixxly-navy)',
                border: '1px dashed var(--color-tixxly-navy)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
              }}
            >
              + Add press article
            </button>
          )}
        </div>
      </div>

      {error && (
        <p style={{ color: 'var(--color-error)', marginTop: '1rem', fontSize: '0.9rem' }}>{error}</p>
      )}

      {success && (
        <p style={{ color: 'var(--color-success)', marginTop: '1rem', fontSize: '0.9rem' }}>{success}</p>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        style={{
          marginTop: '1.5rem',
          padding: '0.5rem 1.5rem',
          background: saving ? 'var(--color-gray-400)' : 'var(--color-tixxly-navy)',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: saving ? 'not-allowed' : 'pointer',
          fontSize: '0.9rem',
        }}
      >
        {saving ? 'Saving...' : 'Save Storefront Identity'}
      </button>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.35rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  color: 'var(--color-gray-700)',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  fontSize: '0.9rem',
}

const hintStyle: React.CSSProperties = {
  fontSize: '0.8rem',
  color: 'var(--color-gray-500)',
  marginTop: '0.25rem',
}
